import customTheme from '@/constants/theme.custom';
import envService from '@/services/env.service';
import { useTheme } from '@cmpsr/components';
import { Stripe, StripeElementsOptionsMode, loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';

const STRIPE_RETRY_BASE_DELAY_MS = 1000;

async function loadStripeWithRetry(key: string, retries = 3): Promise<Stripe | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await loadStripe(key);
    } catch (error: unknown) {
      console.warn(`Stripe load attempt ${attempt + 1} failed:`, error);
      if (attempt === retries) return null;
      // Exponential backoff: 1s, 2s, 4s
      await new Promise(r => setTimeout(r, STRIPE_RETRY_BASE_DELAY_MS * 2 ** attempt));
    }
  }
  return null;
}

let stripePromise: Promise<Stripe | null> | null = null;

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    const publishableKey = envService.getEnv().VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      throw new Error('Stripe publishable key not configured');
    }
    stripePromise = loadStripeWithRetry(publishableKey);
  }
  return stripePromise;
}

export function useStripeLoadStatus(promise: Promise<Stripe | null>): 'loading' | 'ready' | 'failed' {
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    promise.then(s => setStatus(s ? 'ready' : 'failed'));
  }, [promise]);
  return status;
}

type PaymentVariant = 'default' | 'dark' | 'transparent' | 'seamless';
type PaymentMode = 'setup' | 'payment' | 'subscription';

interface UsePaymentControllerOptions {
  variant?: PaymentVariant;
  amount?: number;
  isTrial?: boolean;
  /** Explicit mode override. If not set, mode is inferred from isTrial. */
  mode?: PaymentMode;
  /** Email to prefill in Stripe Link */
  email?: string;
  /** Full name to prefill in billing details */
  name?: string;
}

export default function usePaymentController(
  options: UsePaymentControllerOptions = {}
) {
  const { variant = 'default', amount, isTrial = false, mode: explicitMode, email, name } = options;
  const theme = useTheme();

  const getAppearance = (): StripeElementsOptionsMode['appearance'] => {
    const baseAppearance = {
      theme: 'stripe' as const,
      variables: {
        // Font Family
        fontFamily: 'Inter',
        fontWeightNormal: '400',
        fontWeightMedium: '500',
        fontWeightBold: '700',
        fontSizeBase: '16px',
        fontLineHeight: '1.5',

        // Colors
        colorPrimary: theme.colors['primary-default'],
        colorBackground: theme.colors['background-container-default'],
        colorText: theme.colors['text-primary'],
        colorDanger: customTheme.colors['salmon-light'],
        colorSuccess: theme.colors['alert-success-default'],
        colorWarning: theme.colors['alert-warning-default'],
        colorTextSecondary: theme.colors['text-secondary'],
        colorTextPlaceholder: theme.colors['text-disabled'],

        // Accessible text colors
        accessibleColorOnColorPrimary: theme.colors['text-button-primary'],
        accessibleColorOnColorBackground: theme.colors['text-primary'],
        accessibleColorOnColorSuccess: theme.colors['text-success'],
        accessibleColorOnColorDanger: theme.colors['text-error'],
        accessibleColorOnColorWarning: theme.colors['text-warning'],

        // Spacing and borders
        spacingUnit: '4px',
        borderRadius: theme.radii['radii-2'],

        // Icons
        iconColor: theme.colors['text-primary'],
        iconHoverColor: theme.colors['primary-hover'],
        iconCardErrorColor: theme.colors['alert-error-default'],
        iconCardCvcColor: theme.colors['text-secondary'],
        iconCardCvcErrorColor: theme.colors['alert-error-default'],
        iconCheckmarkColor: theme.colors['alert-success-default'],
        iconChevronDownColor: theme.colors['text-secondary'],
        iconChevronDownHoverColor: theme.colors['primary-hover'],
        iconCloseColor: theme.colors['text-secondary'],
        iconCloseHoverColor: theme.colors['primary-hover'],
        iconLoadingIndicatorColor: theme.colors['secondary-default'],
        iconMenuColor: theme.colors['text-secondary'],
        iconMenuHoverColor: theme.colors['primary-hover'],
        iconPasscodeDeviceColor: theme.colors['text-secondary'],
        iconPasscodeDeviceHoverColor: theme.colors['primary-hover'],
        iconPasscodeDeviceNotificationColor: theme.colors['alert-error-default'],
        iconRedirectColor: theme.colors['text-secondary'],
        tabIconColor: theme.colors['text-secondary'],
        tabIconHoverColor: theme.colors['primary-hover'],
        tabIconSelectedColor: theme.colors['primary-default'],
        tabIconMoreColor: theme.colors['text-secondary'],
        tabIconMoreHoverColor: theme.colors['primary-hover'],

        // Logo colors
        logoColor: 'dark',
        tabLogoColor: 'dark',
        tabLogoSelectedColor: 'dark',
        blockLogoColor: 'dark',
      },
    };

    // https://docs.stripe.com/elements/appearance-api
    switch (variant) {
      case 'transparent':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
            colorText: '#FFFFFF',
            colorBackground: '#FFFFFF',
            colorPrimary: '#FFFFFF',
            colorTextSecondary: '#FFFFFF',
            colorTextPlaceholder: '#FFFFFF',
            colorSuccess: '#4CAF50',
            colorWarning: customTheme.colors['salmon-light'],
            iconColor: '#000000',
            iconHoverColor: '#000000',
            iconCardErrorColor: customTheme.colors['salmon-light'],
            iconCardCvcColor: '#E6E6E6',
            iconCardCvcErrorColor: customTheme.colors['salmon-light'],
            iconCheckmarkColor: '#4CAF50',
            iconChevronDownColor: '#E6E6E6',
            iconChevronDownHoverColor: '#FF6B6B',
            iconCloseColor: '#E6E6E6',
            iconCloseHoverColor: '#FF6B6B',
            iconLoadingIndicatorColor: '#FF6B6B',
            iconMenuColor: '#E6E6E6',
            iconMenuHoverColor: '#FF6B6B',
            iconPasscodeDeviceColor: '#E6E6E6',
            iconPasscodeDeviceHoverColor: '#FF6B6B',
            iconPasscodeDeviceNotificationColor: customTheme.colors['salmon-light'],
            iconRedirectColor: '#FFFFFF',
            tabIconColor: '#000000',
            tabIconHoverColor: '#000000',
            tabIconSelectedColor: '#000000',
            tabIconMoreColor: '#000000',
            tabIconMoreHoverColor: '#000000',
          },
          rules: {
            '.Tab': {
              backgroundColor: 'transparent',
              border: 'none',
            },
            '.Tab--selected': {
              backgroundColor: 'transparent',
              border: 'none',
            },
            '.Input': {
              backgroundColor: 'transparent',
              color: customTheme.colors.white,
              border: `1px solid ${customTheme.colors.white}`,
              borderRadius: theme.radii['radii-4'],
              padding: '0.75rem',
            },
            '.Input:focus': {
              outline: `0 0 0 0.1875rem ${customTheme.colors['salmon-light']}`,
            },
            '.Input::placeholder': {
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
            '.Label': {
              color: customTheme.colors.white,
              fontFamily: 'Inter',
              fontSize: '10px',
              fontWeight: '400',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            },
            '.AccordionItem': {
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              backgroundColor: 'transparent',
              fontWeight: '500',
              boxShadow: 'none',
              padding: '0 0 16px 0',
            },
            '.AccordionItem--selected': {
              color: customTheme.colors.white,
            },
            '.Block': {
              backgroundColor: 'transparent',
            },
          },
        };
      case 'dark':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
            colorText: customTheme.colors.white,
            colorBackground: customTheme.colors.darkBlue,
          },
          rules: {
            '.Tab': {
              border: 'none',
              backgroundColor: customTheme.colors.darkBlue,
            },
            '.Tab--selected': {
              border: 'none',
              backgroundColor: customTheme.colors.darkBlue,
            },
            '.Input': {
              backgroundColor: 'transparent',
              color: customTheme.colors.white,
              border: `1px solid ${customTheme.colors.white}`,
              borderRadius: theme.radii['radii-2'],
              padding: '0.75rem',
            },
            '.Input:focus': {
              outline: `0 0 0 0.1875rem ${customTheme.colors['salmon-light']}`,
            },
            '.Input::placeholder': {
              color: 'rgba(255, 255, 255, 0.6)',
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
            '.Label': {
              color: customTheme.colors.white,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              marginBottom: '0.500rem',
            },
            '.AccordionItem': {
              border: 'none',
              color: customTheme.colors.white,
              fontWeight: '500',
            },
            '.AccordionItem--selected': {
              color: customTheme.colors.white,
            },
          },
        };
      case 'default':
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
          },
          rules: {
            '.AccordionItem': {
              color: theme.colors['text-primary'],
              fontWeight: '500',
              paddingTop: '0px',
              paddingBottom: '12px',
              paddingLeft: '0',
              paddingRight: '0',
              border: 'none',
            },
            '.AccordionItem--selected': {
              color: theme.colors['text-primary'],
            },
            '.Label': {
              color: theme.colors['text-primary'],
              fontFamily: 'Inter',
              fontSize: '10px',
              fontWeight: '400',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            },
            '.Block': {
              borderColor: theme.colors['ui-element-outline-default'],
            },
            '.Input': {
              border: `1px solid ${theme.colors['ui-element-outline-default']}`,
              borderRadius: theme.radii['radii-2'],
              marginBottom: '8px',
            },
            '.Input:focus': {
              outline: '0 0 0 0.1875rem var(--chakra-colors-primary-focus)',
            },
            '.Input::placeholder': {
              color: theme.colors['text-disabled'],
            },
            '.Input:autofill': {
              backgroundColor: 'transparent',
            },
          },
        };
      case 'seamless':
        // Seamless variant for Payment Page V2 - matches funnel FunnelField
        // Inputs: h-14 (56px), rounded-2xl (16px), border-1 border-gray-200,
        // white bg, text-base (16px), text-catch-dark-blue-90, placeholder
        // text-gray-400, focus ring catch-light-blue. Label text-base
        // font-semibold catch-dark-blue-90. Container is transparent so
        // inputs sit on the page bg, not in a slate block.
        return {
          ...baseAppearance,
          variables: {
            ...baseAppearance.variables,
            colorBackground: '#FFFFFF',
            borderRadius: '16px',
            fontSizeBase: '16px', // Match text-base
            colorTextPlaceholder: '#9ca3af', // gray-400
          },
          rules: {
            '.AccordionItem': {
              color: theme.colors['text-primary'],
              fontWeight: '500',
              paddingTop: '0px',
              paddingBottom: '12px',
              paddingLeft: '0',
              paddingRight: '0',
              border: 'none',
              backgroundColor: 'transparent',
            },
            '.AccordionItem--selected': {
              color: theme.colors['text-primary'],
              backgroundColor: 'transparent',
            },
            '.Label': {
              color: '#183344', // catch-dark-blue-90
              fontFamily: 'Inter',
              fontSize: '16px', // text-base
              fontWeight: '600', // font-semibold
              letterSpacing: 'normal',
              textTransform: 'none',
              marginBottom: '8px', // gap-2 between label and input
            },
            '.Block': {
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              borderRadius: '0',
              padding: '0',
            },
            '.Input': {
              backgroundColor: '#FFFFFF',
              border: '1px solid #e5e7eb', // border-gray-200
              borderRadius: '16px', // rounded-2xl
              padding: '16px', // 24px line-height + 16px*2 padding ≈ h-14 (56px)
              fontSize: '16px', // text-base
              lineHeight: '24px',
              color: '#183344', // text-catch-dark-blue-90
              marginBottom: '0px',
              boxShadow: 'none',
            },
            '.Input:focus': {
              border: '1px solid #e5e7eb', // keep stroke
              boxShadow: '0 0 0 2px #afdfff', // ring-catch-light-blue
              outline: 'none',
            },
            '.Input::placeholder': {
              color: '#9ca3af', // gray-400
            },
            '.Input--invalid': {
              border: '1px solid #f87171', // border-red-400
              boxShadow: 'none',
            },
            '.Input:autofill': {
              backgroundColor: '#FFFFFF',
            },
            '.Tab': {
              backgroundColor: '#FFFFFF',
              border: '1px solid #e5e7eb', // border-gray-200
              borderRadius: '16px',
            },
            '.Tab:hover': {
              borderColor: '#d1d5db', // gray-300
            },
            '.Tab--selected': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 2px #afdfff', // ring-catch-light-blue
            },
          },
        };
    }
  };

  const appearance = getAppearance();

  // Determine mode: explicit mode takes precedence, then infer from isTrial
  const mode: PaymentMode = explicitMode ?? (isTrial ? 'payment' : 'subscription');

  // Stripe Elements runs in an isolated iframe and needs explicit font loading
  // This URL matches vite.config.ts Unfonts config to maximize browser cache hits
  const fonts = [
    {
      cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700',
    },
  ];

  const getStripeOptions = (): StripeElementsOptionsMode => {
    switch (mode) {
      case 'setup':
        // Setup mode for saving card without charging (e.g., update payment method)
        return {
          fonts,
          mode: 'setup',
          currency: 'usd',
          paymentMethodTypes: ['card'],
          locale: 'en',
          appearance,
          loader: 'always',
        };
      case 'payment':
        // Payment mode for trials with $1 verification hold
        return {
          fonts,
          mode: 'payment',
          currency: 'usd',
          amount: 100, // $1.00 for card verification
          captureMethod: 'manual', // Must match backend PaymentIntent capture_method
          setupFutureUsage: 'off_session', // Must match backend - attaches payment method to customer
          paymentMethodTypes: ['card'],
          locale: 'en',
          appearance,
          loader: 'always',
        };
      case 'subscription':
        // Subscription mode for regular payments
        return {
          fonts,
          mode: 'subscription',
          currency: 'usd',
          amount: amount,
          paymentMethodTypes: ['card'],
          locale: 'en',
          appearance,
          loader: 'always',
        };
    }
  };

  return { stripePromise: getStripePromise(), stripeOptions: getStripeOptions() };
}
